import React, { useEffect, useState, useCallback } from "react";
import { api, fmtNum, fmtDate, errMsg } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { Switch } from "@finance/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@finance/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { SearchableSelect } from "@finance/components/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { CurrencySelect } from "@finance/components/CurrencySelect";
import { QuickAddButton } from "@finance/components/QuickAdd";
import { DocNumberField } from "@finance/components/DocNumberField";
import { exportXlsx } from "@finance/lib/exportCsv";
import { toast } from "sonner";
import { Plus, Trash, Coins, HandCoins, ArrowClockwise, DownloadSimple, Receipt } from "@phosphor-icons/react";
import { KpiStrip, KpiCard, TableEmpty } from "@finance/components/DesignSystem";
import { ApplyDialog, SettleDialog, TieoutStrip } from "@finance/components/AdvanceApply";

const TYPES = {
  Prepayment: { partyLabel: "Supplier", partyType: "supplier", endpoint: "/suppliers", account: "1450 Prepayments → releases to COGS (5000)", pill: "text-[#1B4383]" },
  "Advance Receipt": { partyLabel: "Client", partyType: "client", endpoint: "/clients", account: "2110 Advance Receipts → releases to Revenue (4010)", pill: "text-emerald-600" },
};

function RecordDialog({ type, banks, parties, refetchParties, onSaved }) {
  const cfg = TYPES[type];
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [openPos, setOpenPos] = useState([]);

  const reset = () => setForm({ currency: "TZS", fx_rate: 1, date: new Date().toISOString().slice(0, 10), po_related: false, allocations: [] });

  const loadPos = useCallback((partyId) => {
    if (!partyId) { setOpenPos([]); return; }
    api.get(`/advances/open-pos?type=${encodeURIComponent(type)}&party_id=${partyId}`)
      .then((r) => setOpenPos(r.data || [])).catch(() => setOpenPos([]));
  }, [type]);

  const addAlloc = () => setForm((f) => ({ ...f, allocations: [...(f.allocations || []), { po_id: "", po_no: "", amount: "" }] }));
  const setAlloc = (i, patch) => setForm((f) => {
    const a = [...(f.allocations || [])]; a[i] = { ...a[i], ...patch }; return { ...f, allocations: a };
  });
  const rmAlloc = (i) => setForm((f) => ({ ...f, allocations: (f.allocations || []).filter((_, x) => x !== i) }));

  const save = async () => {
    if (!form.party_id) { toast.error(`Select a ${cfg.partyLabel.toLowerCase()}`); return; }
    if (!form.bank_account_id) { toast.error("Select a bank account"); return; }
    if (!Number(form.amount) || Number(form.amount) <= 0) { toast.error("Enter an amount"); return; }
    setSaving(true);
    try {
      const payload = {
        type, party_id: form.party_id, date: form.date, currency: form.currency,
        fx_rate: Number(form.fx_rate) || 1, amount: Number(form.amount),
        bank_account_id: form.bank_account_id, po_related: !!form.po_related,
        doc_no: form.doc_no || null,
        auto_recognize: !!(form.po_related && form.auto_recognize),
        allocations: form.po_related ? (form.allocations || []).filter((a) => a.po_id && Number(a.amount) > 0)
          .map((a) => ({ po_id: a.po_id, po_no: a.po_no, amount: Number(a.amount) })) : [],
        notes: form.notes || "",
      };
      await api.post("/advances", payload);
      toast.success(`${type} recorded & posted to GL`);
      setOpen(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const allocSum = (form.allocations || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);

  return (
    <>
      <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid={`record-${cfg.partyType}-advance-btn`}
        onClick={() => { reset(); setOpen(true); }}><Plus size={16} className="mr-1" />Record {type}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="advance-record-dialog">
          <DialogHeader><DialogTitle className="font-display">Record {type}</DialogTitle></DialogHeader>
          <p className="-mt-2 text-xs text-gray-500">{cfg.account}</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="lx-label">{cfg.partyLabel} *</Label>
              <div className="mt-1 flex items-center gap-1">
                <div className="flex-1">
                  <SearchableSelect testid="advance-party" placeholder={`Select ${cfg.partyLabel.toLowerCase()}`}
                    value={form.party_id || ""} onValueChange={(v) => { setForm({ ...form, party_id: v, allocations: [] }); loadPos(v); }}
                    options={parties.map((p) => ({ value: p.id, label: p.name }))} />
                </div>
                <QuickAddButton type={cfg.partyType} onCreated={(p) => refetchParties().then(() => p?.id && setForm((f) => ({ ...f, party_id: p.id })))} />
              </div>
            </div>
            <div>
              <Label className="lx-label">Date</Label>
              <DateField data-testid="advance-date" className="mt-1" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <DocNumberField docKey={type === "Prepayment" ? "prepayment" : "advance_receipt"} label="Reference No" testid="advance-doc-no" value={form.doc_no} onChange={(v) => setForm({ ...form, doc_no: v })} />
            </div>
            <div>
              <Label className="lx-label">Bank Account *</Label>
              <SearchableSelect className="mt-1" testid="advance-bank" placeholder="Select bank"
                value={form.bank_account_id || ""} onValueChange={(v) => setForm({ ...form, bank_account_id: v })}
                options={banks.map((b) => ({ value: b.id, label: `${b.bank_name} (${b.currency})` }))} />
            </div>
            <div>
              <Label className="lx-label">Currency</Label>
              <div className="mt-1"><CurrencySelect value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })} testid="advance-currency" /></div>
            </div>
            {form.currency && form.currency !== "TZS" && (
              <div>
                <Label className="lx-label">FX Rate → TZS</Label>
                <Input type="number" data-testid="advance-fx" className="mt-1" value={form.fx_rate || ""} onChange={(e) => setForm({ ...form, fx_rate: e.target.value })} />
              </div>
            )}
            <div>
              <Label className="lx-label">Amount ({form.currency || "TZS"}) *</Label>
              <Input type="number" data-testid="advance-amount" className="mt-1" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              {form.currency && form.currency !== "TZS" && Number(form.amount) > 0 && (
                <p className="mt-1 text-xs text-gray-500">≈ TSh {fmtNum(Number(form.amount) * (Number(form.fx_rate) || 1))}</p>
              )}
            </div>
            <div className="col-span-2 flex items-center justify-between rounded-sm border border-gray-200 px-3 py-2 dark:border-[#243049]">
              <Label className="lx-label">Linked To Open POs?</Label>
              <Switch data-testid="advance-po-toggle" checked={!!form.po_related} onCheckedChange={(v) => setForm({ ...form, po_related: v })} />
            </div>
            {form.po_related && (
              <div className="col-span-2 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="lx-label">PO Allocations</Label>
                  <Button type="button" size="sm" variant="outline" data-testid="advance-add-alloc" onClick={addAlloc}><Plus size={14} className="mr-1" />Add PO</Button>
                </div>
                {(form.allocations || []).length === 0 && <p className="text-xs text-gray-400">Add one or more POs and split the amount across them.</p>}
                {(form.allocations || []).map((a, i) => {
                  const po = openPos.find((p) => p.po_id === a.po_id);
                  const over = po && Number(a.amount) > (po.outstanding || 0) + 0.01;
                  return (
                    <div key={i} className="space-y-1" data-testid={`advance-alloc-row-${i}`}>
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <SearchableSelect testid={`advance-alloc-po-${i}`} placeholder="Select PO"
                            value={a.po_id || ""} onValueChange={(v) => { const p = openPos.find((x) => x.po_id === v); setAlloc(i, { po_id: v, po_no: p?.po_no }); }}
                            options={openPos.map((p) => ({ value: p.po_id, label: `${p.po_no} · out ${fmtNum(p.outstanding)}` }))} />
                        </div>
                        <Input type="number" placeholder="Amount" className="w-32" data-testid={`advance-alloc-amt-${i}`} value={a.amount || ""} onChange={(e) => setAlloc(i, { amount: e.target.value })} />
                        <Button type="button" size="icon" variant="ghost" data-testid={`advance-alloc-rm-${i}`} onClick={() => rmAlloc(i)}><Trash size={14} /></Button>
                      </div>
                      {po && (
                        <p className={`pl-1 text-xs ${over ? "font-medium text-red-600" : "text-gray-500"}`} data-testid={`advance-alloc-hint-${i}`}>
                          PO {po.po_no}: outstanding {fmtNum(po.outstanding)} of total {fmtNum(po.total)}{over ? " — allocation exceeds outstanding!" : ""}
                        </p>
                      )}
                    </div>
                  );
                })}
                {(form.allocations || []).length > 0 && (
                  <p className={`text-xs ${allocSum - (Number(form.amount) || 0) > 0.01 ? "text-red-600" : "text-gray-500"}`}>
                    Allocated {fmtNum(allocSum)} of {fmtNum(Number(form.amount) || 0)}
                  </p>
                )}
              </div>
            )}
            <div className="col-span-2">
              <Label className="lx-label">Notes</Label>
              <Input data-testid="advance-notes" className="mt-1" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" disabled={saving} data-testid="advance-save" onClick={save}>{saving ? "Saving…" : "Record & Post"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RecognizeDialog({ row, onDone }) {
  const isPrepay = row.type === "Prepayment";
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/advances/${row.id}/recognition`).then((r) => {
      setInfo(r.data); setAmount(r.data.suggested > 0 ? String(r.data.suggested) : "");
    }).catch(() => {});
  }, [row.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const recognize = async (auto) => {
    setSaving(true);
    try {
      const body = auto ? { auto: true } : { amount: Number(amount) };
      const { data } = await api.post(`/advances/${row.id}/recognize`, body);
      toast.success(`Recognized · ${row.type === "Prepayment" ? "COGS 5000" : "Revenue 4010"} TSh ${fmtNum(data.amount_tzs)}`);
      load(); onDone();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const reverse = async (rid) => {
    if (!window.confirm("Reverse this recognition? Its GL entry is removed.")) return;
    try { await api.delete(`/advances/recognitions/${rid}`); toast.success("Recognition reversed"); load(); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <Button size="sm" variant="outline" className="h-8" data-testid={`advance-recognize-${row.ref_no}`}
        disabled={row.open_balance <= 0.01} onClick={() => setOpen(true)}>
        <ArrowClockwise size={14} className="mr-1" />Recognize
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="advance-recognize-dialog">
          <DialogHeader><DialogTitle className="font-display">Recognize {row.ref_no} → {isPrepay ? "COGS (5000)" : "Revenue (4010)"}</DialogTitle></DialogHeader>
          {!info ? <p className="text-sm text-gray-400">Loading…</p> : (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3 text-sm">
                <Stat label="Advance" v={fmtNum(info.amount)} />
                <Stat label="Recognized" v={fmtNum(info.recognized)} />
                <Stat label="Remaining" v={fmtNum(info.remaining)} />
                <Stat label={info.driver === "proportional" ? `Driver ${info.driver_pct}%` : "Manual"} v={fmtNum(info.driver_recognizable)} />
              </div>
              <p className="text-xs text-gray-500">
                {info.driver === "proportional"
                  ? `Proportional driver: ${isPrepay ? "received/GRV" : "delivered"}-value % across linked POs. Suggested to recognize now: ${fmtNum(info.suggested)}.`
                  : "This advance is not linked to POs — recognize manually as goods are received/delivered."}
              </p>
              {(info.per_allocation || []).length > 0 && (
                <div className="rounded-sm border border-gray-200 dark:border-[#243049]">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-[#0f1729]"><tr className="lx-label">
                      <th className="px-3 py-1.5 text-left">PO</th><th className="px-3 py-1.5 text-right">Allocated</th>
                      <th className="px-3 py-1.5 text-right">{isPrepay ? "Received" : "Delivered"} %</th><th className="px-3 py-1.5 text-right">Recognizable</th>
                    </tr></thead>
                    <tbody>
                      {info.per_allocation.map((p, i) => (
                        <tr key={i} className="border-t border-gray-100 dark:border-[#1b2436]">
                          <td className="px-3 py-1.5">{p.po_no}</td>
                          <td className="px-3 py-1.5 text-right lx-num">{fmtNum(p.amount)}</td>
                          <td className="px-3 py-1.5 text-right lx-num">{p.driver_pct}%</td>
                          <td className="px-3 py-1.5 text-right lx-num">{fmtNum(p.recognizable)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="lx-label">Amount to recognize ({info.currency})</Label>
                  <Input type="number" data-testid="recognize-amount" className="mt-1" value={amount} onChange={(e) => setAmount(e.target.value)} />
                </div>
                <Button className="bg-[#1B4383] hover:bg-[#122E5C]" disabled={saving || !(Number(amount) > 0)} data-testid="recognize-post" onClick={() => recognize(false)}>Recognize</Button>
              </div>
              {(info.recognitions || []).length > 0 && (
                <div>
                  <Label className="lx-label text-gray-500">History</Label>
                  <div className="mt-1 space-y-1">
                    {info.recognitions.map((r) => (
                      <div key={r.id} className="flex items-center justify-between rounded-sm bg-gray-50 px-3 py-1.5 text-xs dark:bg-[#0f1729]" data-testid={`recognition-${r.id}`}>
                        <span>{fmtDate(r.date)} · {fmtNum(r.amount)} {info.currency} (TSh {fmtNum(r.amount_tzs)}) · {r.driver === "proportional" ? `${r.driver_pct}%` : "manual"}</span>
                        <Button size="icon" variant="ghost" className="h-6 w-6" data-testid={`recognition-reverse-${r.id}`} onClick={() => reverse(r.id)}><Trash size={12} /></Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Stat({ label, v }) {
  return (
    <div className="rounded-sm border border-gray-200 p-2 dark:border-[#243049]">
      <div className="lx-label text-gray-500">{label}</div>
      <div className="mt-0.5 font-display text-sm font-bold lx-num dark:text-white">{v}</div>
    </div>
  );
}

function RegisterTable({ type, rows: allRows, banks, parties, refetchParties, onChanged }) {
  const cfg = TYPES[type];
  const [st, setSt] = useState("all");
  const rows = st === "all" ? allRows : allRows.filter((r) => (r.status || "Open") === st);
  const totals = rows.reduce((t, r) => ({
    amount: t.amount + (r.amount || 0), recognized: t.recognized + (r.recognized_amount || 0), open: t.open + (r.open_balance || 0),
  }), { amount: 0, recognized: 0, open: 0 });
  const refunded = allRows.filter((r) => r.status === "Refunded");

  const del = async (r) => {
    if (!window.confirm(`Delete ${r.ref_no}? This reverses its GL posting.`)) return;
    try { await api.delete(`/advances/${r.id}`); toast.success("Deleted & GL reversed"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const exportSnapshot = async () => {
    try {
      const { data } = await api.get("/advances/snapshot");
      const rowsForType = type === "Prepayment" ? data.prepayments : data.advance_receipts;
      if (!rowsForType.length) { toast.info("Nothing open to export."); return; }
      const cols = [
        { key: "ref_no", label: "Ref" }, { key: "date", label: "Date" }, { key: "party", label: cfg.partyLabel },
        { key: "currency", label: "Currency" }, { key: "amount", label: "Amount", format: fmtNum },
        { key: "recognized", label: "Recognized", format: fmtNum }, { key: "open", label: "Open", format: fmtNum },
        { key: "pos", label: "POs" }, { key: "days", label: "Age (days)" }, { key: "bucket", label: "Aging" },
        { key: "status", label: "Status" },
      ];
      exportXlsx(`advances-snapshot-${type === "Prepayment" ? "prepayments" : "advance-receipts"}-${data.as_of}`, rowsForType, cols);
      toast.success("Snapshot exported");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs text-gray-500">{cfg.account}</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid={`export-snapshot-${cfg.partyType}`} onClick={exportSnapshot}><DownloadSimple size={16} className="mr-1" />Export Snapshot</Button>
          <RecordDialog type={type} banks={banks} parties={parties} refetchParties={refetchParties} onSaved={onChanged} />
        </div>
      </div>
      <TieoutStrip type={type} />
      <div className="mb-3 flex flex-wrap gap-1.5">{["all", "Requested", "Open", "Partly Applied", "Fully Applied", "Refunded"].map((s) => (
        <button key={s} data-testid={`adv-status-${cfg.partyType}-${s}`} onClick={() => setSt(s)} className={`rounded-full border px-3 py-1 text-xs ${st === s ? "border-[#1B4383] bg-[#1B4383] text-white" : "border-gray-200"}`}>{s === "all" ? "All" : s}</button>))}</div>
      <KpiStrip cols={5}>
        <KpiCard icon={Receipt} label="Records" value={rows.length} testid={`kpi-adv-count-${cfg.partyType}`} />
        <KpiCard icon={Coins} label="Total Amount" value={fmtNum(totals.amount, "TZS")} testid={`kpi-adv-amount-${cfg.partyType}`} />
        <KpiCard icon={ArrowClockwise} label="Applied / used" value={fmtNum(totals.recognized, "TZS")} tone="success" testid={`kpi-adv-recognized-${cfg.partyType}`} />
        <KpiCard icon={HandCoins} label="Open Balance" value={fmtNum(totals.open, "TZS")} tone="accent" testid={`kpi-adv-open-${cfg.partyType}`} />
        <KpiCard icon={Coins} label="Refunded" value={`${refunded.length} · ${fmtNum(refunded.reduce((s, r) => s + (r.refunded_amount || 0), 0), "TZS")}`} testid={`kpi-adv-refunded-${cfg.partyType}`} />
      </KpiStrip>
      <div className="overflow-hidden rounded-sm border border-gray-200 bg-white dark:border-[#243049] dark:bg-[#0b1220]">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-[#0f1729]"><tr className="lx-label border-b border-gray-200 dark:border-[#243049]">
            <th className="px-4 py-2 text-left">Ref</th>
            <th className="px-4 py-2 text-left">Date</th>
            <th className="px-4 py-2 text-left">{cfg.partyLabel}</th>
            <th className="px-4 py-2 text-left">Bank</th>
            <th className="px-4 py-2 text-left">POs</th>
            <th className="px-4 py-2 text-right">Amount</th>
            <th className="px-4 py-2 text-right">Applied</th>
            <th className="px-4 py-2 text-right">Open</th>
            <th className="px-4 py-2"></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <TableEmpty colSpan={9} title={`No ${type.toLowerCase()}s yet`} message="Records will appear here once you add one." />}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`advance-row-${r.ref_no}`} className="border-b border-gray-100 hover:bg-gray-50 dark:border-[#1b2436] dark:hover:bg-[#131c30]">
                <td className={`px-4 py-2 font-semibold ${cfg.pill}`}>
                  {r.ref_no}
                  {r.is_request && <span className="ml-1 rounded-sm bg-amber-200 px-1 text-[10px] font-bold text-amber-900" data-testid={`advance-request-badge-${r.ref_no}`}>Request</span>}
                  {r.status && r.status !== "Open" && (
                    <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-medium ${r.status === "Recognized" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{r.status}</span>
                  )}
                  {r.auto_recognize && (
                    <span className="ml-1 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-medium text-sky-700" data-testid={`advance-auto-badge-${r.ref_no}`}>Auto</span>
                  )}
                </td>
                <td className="px-4 py-2">{fmtDate(r.date)}</td>
                <td className="px-4 py-2 font-medium">{r.party_name}</td>
                <td className="px-4 py-2 text-gray-500">{r.bank_name}</td>
                <td className="px-4 py-2 text-gray-500">
                  {r.po_related ? (r.allocations || []).map((a) => a.po_no).filter(Boolean).join(", ") || "—" : <span className="text-gray-400">Unlinked</span>}
                </td>
                <td className="px-4 py-2 text-right lx-num">{r.currency !== "TZS" ? `${r.currency} ` : ""}{fmtNum(r.amount)}</td>
                <td className="px-4 py-2 text-right lx-num">{fmtNum(r.recognized_amount)}</td>
                <td className="px-4 py-2 text-right lx-num font-semibold">{fmtNum(r.open_balance)}</td>
                <td className="px-4 py-2 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {r.status === "Requested" ? <SettleDialog row={r} banks={banks} onDone={onChanged} label={type === "Prepayment" ? "Pay deposit" : "Record deposit"} /> : <ApplyDialog row={r} onDone={onChanged} />}
                    {r.status !== "Requested" && r.status !== "Refunded" && r.open_balance > 0.01 && <Button size="sm" variant="ghost" className="h-8 text-xs" data-testid={`advance-refund-${r.ref_no}`} onClick={async () => { const b = window.prompt(`Refund open balance via bank — type the bank name exactly:\n${banks.map((x) => x.bank_name).join("\n")}`); const bank = banks.find((x) => x.bank_name === b); if (!bank) return; try { await api.post(`/advances/${r.id}/mark-refunded`, { bank_account_id: bank.id }); toast.success("Refunded"); onChanged(); } catch (e) { toast.error(errMsg(e)); } }}>Refund</Button>}
                    <Button size="icon" variant="ghost" data-testid={`advance-delete-${r.ref_no}`} onClick={() => del(r)}><Trash size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length > 0 && (
              <tr className="border-t-2 border-gray-300 font-bold dark:border-[#2c3852]">
                <td className="px-4 py-2" colSpan={5}>TOTAL ({rows.length})</td>
                <td className="px-4 py-2 text-right lx-num">{fmtNum(totals.amount)}</td>
                <td className="px-4 py-2 text-right lx-num">{fmtNum(totals.recognized)}</td>
                <td className="px-4 py-2 text-right lx-num text-[#1B4383]">{fmtNum(totals.open)}</td>
                <td></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function AdvancesRegister() {
  const [prepayments, setPrepayments] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [banks, setBanks] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [clients, setClients] = useState([]);

  const loadAll = useCallback(() => {
    api.get("/advances?type=Prepayment").then((r) => setPrepayments(r.data || [])).catch(() => {});
    api.get("/advances?type=Advance Receipt").then((r) => setAdvances(r.data || [])).catch(() => {});
  }, []);
  const loadSuppliers = useCallback(() => api.get("/suppliers").then((r) => setSuppliers(r.data || [])), []);
  const loadClients = useCallback(() => api.get("/clients").then((r) => setClients(r.data || [])), []);

  useEffect(() => {
    loadAll();
    api.get("/bank-accounts").then((r) => setBanks(r.data || [])).catch(() => {});
    loadSuppliers(); loadClients();
  }, [loadAll, loadSuppliers, loadClients]);

  return (
    <Layout>
      <PageHeader title="Prepayments & Advances" subtitle="Prepayments to suppliers (1450) and advance receipts from clients (2110). Used only through Apply to invoice — no P&L release, no VAT. Staff advances live under Expenses as Allowance Advances." />
      <div className="p-8">
        <Tabs defaultValue="prepayments">
          <TabsList data-testid="advances-tabs">
            <TabsTrigger value="prepayments" data-testid="tab-prepayments"><Coins size={15} className="mr-1" />Prepayments (to Suppliers)</TabsTrigger>
            <TabsTrigger value="advances" data-testid="tab-advances"><HandCoins size={15} className="mr-1" />Advance Receipts (from Clients)</TabsTrigger>
          </TabsList>
          <TabsContent value="prepayments" className="mt-6">
            <RegisterTable type="Prepayment" rows={prepayments} banks={banks} parties={suppliers} refetchParties={loadSuppliers} onChanged={loadAll} />
          </TabsContent>
          <TabsContent value="advances" className="mt-6">
            <RegisterTable type="Advance Receipt" rows={advances} banks={banks} parties={clients} refetchParties={loadClients} onChanged={loadAll} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
